import assert from 'assert';
import lodashGet from 'lodash/fp/get';

import {
  USER_ROLE_ANONYMOUS,
} from 'common/userRoles';

import log from 'client/lib/log';
import makeApplicationContext from 'client/analytics/makeApplicationContext';

import { ANALYTICS_EVENT_PAGE_VIEWED } from 'client/analytics/eventList';

const { NODE_ENV } = process.env;
const debug = log.debug('sunroom:analytics');

/**
 * Transforms a user data object to standard analytics traits.
 *
 * https://segment.com/docs/spec/identify/#traits
 *
 * @param  {Object} userData User data object to transform.
 * @return {Object} Standard identification traits.
 */
export function transformUserDataToSegmentIdentifyTraits(userData) {
  const [ firstName, ...lastNameParts ] = (userData.name || '').split(' ');

  return {
    avatar: userData.profileImage || undefined,
    email: userData.email || undefined,
    firstName: firstName || undefined,
    lastName: lastNameParts.join(' ') || undefined,
    name: userData.name || undefined,
    id: userData.id || undefined,
    phone: userData.phone || undefined,
    createdAt: userData.createdDate || undefined,
  };
}

/**
 * https://segment.com/docs/spec/common/
 *
 * @param  {Object} state
 * @return {Object} Common event parameters
 */
function makeCommonEventParams(state, navigationContext, integrations) {
  const {
    currentUser,
    analytics: {
      analyticsSessionId,
      utmValues,
      requestIp,
      location,
    },
  } = state;

  const eventParams = {
    context: {
      app: {
        name: 'SunroomWeb',
        namespace: `com.${NODE_ENV === 'production' ? 'production' : 'development'}.sunroom`,
      },
      ip: requestIp,
      location: location,
      screen: {
        width: navigationContext.browserWidth,
        height: navigationContext.browserHeight,
      },
    },
  };

  if (integrations) {
    eventParams.integrations = integrations;
  }

  if (currentUser.roles.includes(USER_ROLE_ANONYMOUS)) {
    eventParams.anonymousId = analyticsSessionId;
  }
  else if (currentUser.id) {
    eventParams.userId = String(currentUser.id);
  }

  if (utmValues && typeof utmValues === 'object') {
    eventParams.context.campaign = {
      name: (utmValues.campaign || undefined),
      source: (utmValues.source || undefined),
      medium: (utmValues.medium || undefined),
      content: (utmValues.content || undefined),
      term: (utmValues.term || undefined),
    };
  }

  return eventParams;
}

/**
 * Extracts relevant contextual data from a Window object.
 *
 * @param  {Window} _window  The global "window" object.
 * @return {Object} Relevant contextual metadata.
 */
function extractNavigationContextFromWindow(_window) {
  return {
    documentTitle: lodashGet('document.title', _window),
    documentReferrer: lodashGet('document.referrer', _window),
    locationHref: lodashGet('location.href', _window),
    locationPathname: lodashGet('location.pathname', _window),
    locationHostname: lodashGet('location.hostname', _window),
    locationSearch: lodashGet('location.search', _window),
    locationPort: lodashGet('location.port', _window),
    locationProtocol: lodashGet('location.protocol', _window),
    locationHash: lodashGet('location.hash', _window),
    browserWidth: _window.innerWidth
      || lodashGet('documentElement.clientWidth', _window.document)
      || lodashGet('body.clientWidth', _window.document),
    browserHeight: _window.innerHeight
      || lodashGet('documentElement.clientHeight', _window.document)
      || lodashGet('body.clientHeight', _window.document),
    scrollVertical: (
      (_window.pageYOffset || lodashGet('documentElement.scrollTop', _window.document))
      - (lodashGet('documentElement.clientTop', _window.document) || 0)
    ),
  };
}

export default {
  /**
   * Configures the analytics service instance.
   *
   * @param  {Window}   args._window  Window object.
   * @param  {Object}   args.store    Baobab store.
   * @param  {Function} args.onTrack  Function to be run after tracking an event.
   * @return {undefined}
   */
  configure({ _window, store, onTrack }) {
    this.store = store;
    this._window = _window;
    this.onTrack = onTrack;
  },

  /**
   * Checks if analytics is enabled for this user session.
   *
   * @return {boolean} True if analytics is enabled for this user session.
   */
  isAnalyticsEnabled() {
    const state = this.store.get();
    return (
      process.browser
      && state.analytics.isEnabled
      && !state.analytics.userAgent.isCrawler
    );
  },

  /**
   * Identifies a user.
   */
  identify() {
    const analyticsEnabled = this.isAnalyticsEnabled();
    const state = this.store.get();
    const userId = state.currentUser.id || state.analytics.analyticsSessionId;
    const isAnonymous = state.currentUser.roles.includes(USER_ROLE_ANONYMOUS);
    const navigationContext = extractNavigationContextFromWindow(this._window);
    const segmentTraits = isAnonymous
      ? null
      : transformUserDataToSegmentIdentifyTraits(state.currentUser || {});

    const integrations = isAnonymous
      ? {
        All: true,
        'Customer.io': false,
      }
      : null;
    const commonEventParams = makeCommonEventParams(state, navigationContext, integrations);

    debug('Identifying', {
      user: state.currentUser,
      isEnabled: analyticsEnabled,
      segmentTraits: segmentTraits,
      navigationContext: navigationContext,
      commonEventParams: commonEventParams,
    });

    if (analyticsEnabled) {
      this.safeAnalyticsCall('identify', String(userId), segmentTraits, commonEventParams);
    }
  },

  /**
   * Creates a user alias. This should be called after a user signup to attribute his old events to his new ID.
   *
   * https://segment.com/docs/spec/alias/
   *
   * @param {string|number} userId     The new user ID.
   * @param {string|number} userPrevId The previous user ID.
   */
  alias(userId, userPrevId) {
    const analyticsEnabled = this.isAnalyticsEnabled();

    debug('Aliasing', { userId, userPrevId, analyticsEnabled });

    if (analyticsEnabled) {
      this.safeAnalyticsCall('alias', String(userId), userPrevId);
    }
  },

  /**
   * Clears the analytics user state.
   */
  logout() {
    const analyticsEnabled = this.isAnalyticsEnabled();
    debug('Logging out', { analyticsEnabled });

    if (analyticsEnabled) {
      // NOTE(@d-oliveros): window.analytics.reset() is the "new" way of resetting cookies.
      const analyticsResetWasCalled = this.safeAnalyticsCall('reset');

      // NOTE(@d-oliveros): window.analytics.user.logout() is the "old" way of resetting cookies. Not sure if needed anymore.
      if (!analyticsResetWasCalled && lodashGet('analytics.user.logout', this._window)) {
        this._window.analytics.user.logout();
      }
      if (lodashGet('mixpanel.reset', this._window)) {
        this._window.mixpanel.reset();
      }
    }
  },

  /**
   * Tracks an event.
   *
   * @param {string} eventType                    The event type.
   * @param {Object} originalEventProps           Extra properties to be attached to this event.
   * @param {Object} originalEventTrackingOptions Extra segment options to enable/disable only certain integrations, etc.
   */
  track(eventType, originalEventProps = {}, originalEventTrackingOptions = {}) {
    const analyticsEnabled = this.isAnalyticsEnabled();
    const state = this.store.get();
    const navigationContext = extractNavigationContextFromWindow(this._window);

    const applicationContext = makeApplicationContext({
      state: state,
      navigationContext: navigationContext,
      pageViewCount: this.getPageViewCount(),
    });

    const eventPayload = { ...applicationContext, ...originalEventProps };

    const commonEventParams = makeCommonEventParams(state, navigationContext);
    const eventTrackingOptions = { ...commonEventParams, ...originalEventTrackingOptions };

    const event = {
      type: eventType,
      payload: eventPayload,
      trackingOptions: eventTrackingOptions,
    };

    debug(`Tracking "${event.type}"`, { event, analyticsEnabled });

    if (analyticsEnabled) {
      // Track the event through Segment
      this.safeAnalyticsCall('track', event.type, event.payload, event.trackingOptions);

      this._eventLog = this._eventLog || [];
      this._eventLog.push({
        timestamp: Date.now(),
        event: event,
      });

      // Run any custom tracking handler
      if (typeof this.onTrack === 'function') {
        this.onTrack(event);
      }
    }
  },

  /**
   * Tracks a page view.
   */
  pageview() {
    const analyticsEnabled = this.isAnalyticsEnabled();
    const state = this.store.get();
    const navigationContext = extractNavigationContextFromWindow(this._window);

    if (analyticsEnabled) {
      const applicationContext = makeApplicationContext({
        state: state,
        navigationContext: extractNavigationContextFromWindow(this._window),
        pageViewCount: this.getPageViewCount(),
      });

      this.safeAnalyticsCall(
        'page',
        state.analytics.screenId,
        navigationContext.documentTitle,
        applicationContext,
      );
    }

    this.track(ANALYTICS_EVENT_PAGE_VIEWED);
  },

  getPageViewCount() {
    const byPageLoad = ({ event }) => event.type === ANALYTICS_EVENT_PAGE_VIEWED;
    this._eventLog = this._eventLog || [];
    const pageViewCount = this._eventLog.filter(byPageLoad).length;
    return pageViewCount;
  },

  /**
   * Makes a safe "window.analytics" call.
   *
   * @param  {string}  methodName  Method in Segment's analytics library to call
   * @param  {Array}   args        Destructured array with remaining function arguments
   * @return {boolean}             True if the method was called
   */
  safeAnalyticsCall(methodName, ...args) {
    assert(methodName, 'methodName is required.');

    if (typeof lodashGet(`analytics.${methodName}`, this._window) !== 'function') {
      return false;
    }

    try {
      this._window.analytics[methodName](...args);
    }
    catch (error) {
      log.error(error);
    }

    return true;
  },
};
